import axios from 'axios';
import { tauriAxiosAdapter } from './tauriAxiosAdapter';
import { attachAuthHeaders } from './clientId';
import { isNetworkError } from './networkError';
import {
  ensureFreshToken,
  refreshNow,
  hasRefreshableSession,
  clearTokens,
} from '../services/tokenRefresher';

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || 'https://api.apiboo.com/api/v1',
  timeout: 10000,
  adapter: tauriAxiosAdapter,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor: proactive token refresh (single-flight), then headers.
apiClient.interceptors.request.use(
  async (config) => {
    await ensureFreshToken().catch(() => {});
    return attachAuthHeaders(config);
  },
  (error) => Promise.reject(error)
);

// Response interceptor: one refresh + one retry on 401, then soft logout event.
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Card X1: "this device was removed from the vault's device list" is a 401
    // no refresh can fix and no reason to sign the whole app out — the caller
    // (the vault) says so and offers a new sign-in.
    if (isDeviceRevoked(error)) return Promise.reject(error);
    if (error.response?.status === 401) {
      const config = error.config || {};

      if (!config._authRetried && hasRefreshableSession()) {
        try {
          await refreshNow();
          config._authRetried = true;
          return apiClient.request(config);
        } catch (refreshError) {
          // Same rule as apiService.js's interceptor: a refresh that could
          // not REACH the IdP is not evidence the refresh token is bad, and
          // clearTokens() below is unrecoverable (it drops the refresh token
          // too). Keep the session and surface the original 401.
          if (isNetworkError(refreshError)) {
            return Promise.reject(error);
          }
          // fall through to logout event
        }
      }

      clearTokens();
      // Don't redirect automatically, let the auth store handle it
      window.dispatchEvent(new CustomEvent('auth:logout'));
    }
    return Promise.reject(error);
  }
);

export { apiClient };

/** A 401 that means "this device was removed from the vault" (card X1), not a dead session. */
export function isDeviceRevoked(error) {
  return error?.response?.status === 401 && error?.response?.data?.error === 'device_revoked';
}

/**
 * Pulls the human-readable reason out of a failed billing call.
 *
 * The billing endpoints answer with `{"error": "..."}` (Phoenix convention),
 * so reading only `data.message` — as these call sites used to — threw away
 * every server-supplied reason and left users staring at "Request failed with
 * status code 500".
 */
export function billingErrorMessage(err, fallback) {
  const data = err?.response?.data;
  return data?.error || data?.message || err?.message || fallback;
}

/**
 * The FAGG § 16 / § 18 consent fields for a NEW paid contract (checkout, or a
 * plan change that starts one). `immediate_start_consent` is true only when the
 * user ticked the box themselves — never a default — and `consent_text_version`
 * names the exact wording they saw, so the backend can store
 * `{user, plan, text_version, timestamp}` as evidence.
 */
function consentFields(consent) {
  if (!consent) return {};
  return {
    immediate_start_consent: consent.immediateStartConsent === true,
    consent_text_version: String(consent.consentTextVersion || ''),
  };
}

export async function createCheckoutSession(priceId, currency, consent) {
  const body = { price_id: priceId, ...consentFields(consent) };
  if (currency === 'usd' || currency === 'eur') body.currency = currency;
  const res = await apiClient.post('/billing/checkout', body);
  return res.data.url;
}

export async function changeSubscription(priceId, consent) {
  const res = await apiClient.post('/billing/change-plan', { price_id: priceId, ...consentFields(consent) });
  return res.data; // { success, plan, billing_cycle }
}

/**
 * § 13a FAGG "Vertrag widerrufen": declares the withdrawal for the signed-in
 * user's subscription. The backend records the receipt time, decides whether
 * the 14-day period still runs, cancels and refunds if it does, and sends the
 * receipt email itself.
 *
 * Answers `{ status: "received", withdrawal_id, received_at, eligible,
 * cancelled, refund: { amount_cents, currency } | null }`.
 */
export async function requestWithdrawal(reason) {
  const text = typeof reason === 'string' ? reason.trim() : '';
  const res = await apiClient.post('/billing/withdrawal', text ? { reason: text } : {});
  return res.data;
}

export async function createBillingPortalSession() {
  const res = await apiClient.post('/billing/portal');
  return res.data.url;
}

export async function getPlans() {
  const res = await apiClient.get('/plans');
  return res.data.plans;
}

export async function createAiTokenCheckout(priceId) {
  const res = await apiClient.post('/billing/ai-tokens', { price_id: priceId });
  return res.data.url;
}

export async function getInvoices() {
  const res = await apiClient.get('/billing/invoices');
  return res.data.invoices;
}
